import type { ClientUser } from '../shared/types/user'
import { getDiscogsCollectionPage } from './api/collection'
import { closeAppEvents, initAppEvents } from './api/events'
import { flushPendingSettings } from './api/settings'
import { getUser } from './api/user'
import { openOverlay } from './components/Overlay'
import ProtectedLayout from './components/ProtectedLayout'
import { maybeShowReleaseNotesDialog } from './components/ReleaseNotesDialog'
import { renderRelease, renderReleaseLazy } from './components/release/Release'
import ActivityPage from './pages/activity/ActivityPage'
import CollectionPage from './pages/collection/CollectionPage'
import LoginPage from './pages/login/LoginPage'
import { PrivacyPage, SupportPage } from './pages/public/PublicInfoPage'
import { SettingsAppOverlayController } from './pages/settings/SettingsAppOverlayController'
import SettingsPage from './pages/settings/SettingsPage'
import {
	resolveSettingsOverlay,
	settingsOverlayPath,
} from './pages/settings/settingsOverlayRoutes'
import { openSettingsSupportOverlay } from './pages/settings/settingsSupportOverlay'
import ShufflePage from './pages/shuffle/ShufflePage'
import type { CollectionReadinessContext } from './stores/collectionStore'
import { getCachedReleaseByReleaseId } from './stores/collectionStore'
import { rememberAppRoute } from './utils/bugReportContext'

/** Mount once, suspend on navigation, and dispose only when the session ends. */
export interface PageHandle {
	show(): void
	hide(): void
	dispose(): void
	refresh?(): Promise<void>
}

export interface PageMountContext {
	containerEl: HTMLElement
	user: ClientUser | null
	collection?: CollectionReadinessContext
	signal: AbortSignal
}

export interface PageComponent {
	mount(context: PageMountContext): PageHandle | Promise<PageHandle>
}

export interface Route {
	component: PageComponent
	icon: string
	isProtected: boolean
	collectionDependent?: boolean
	refreshable?: boolean
	title: string
}

let navigationSequence = 0
let shell: ProtectedLayout | undefined
let publicPage: PageHandle | undefined
let publicAbort: AbortController | undefined

export const routeEntries: [string, Route][] = [
	[
		'/shuffle',
		{
			component: ShufflePage,
			icon: '#symbols-shuffle',
			isProtected: true,
			collectionDependent: true,
			refreshable: true,
			title: 'Shuffle',
		},
	],
	[
		'/collection',
		{
			component: CollectionPage,
			icon: '#symbols-collection',
			isProtected: true,
			collectionDependent: true,
			refreshable: true,
			title: 'Collection',
		},
	],
	[
		'/activity',
		{
			component: ActivityPage,
			refreshable: true,
			icon: '#symbols-zap',
			isProtected: true,
			title: 'Activity',
		},
	],
	[
		'/settings',
		{
			component: SettingsPage,
			icon: '#symbols-settings',
			isProtected: true,
			title: 'Settings',
		},
	],
	[
		'/login',
		{
			component: LoginPage,
			icon: '#symbols-login',
			isProtected: false,
			title: 'Login',
		},
	],
	[
		'/privacy',
		{
			component: PrivacyPage,
			icon: '#symbols-settings',
			isProtected: false,
			title: 'Privacy Policy',
		},
	],
	[
		'/support',
		{
			component: SupportPage,
			icon: '#symbols-settings',
			isProtected: false,
			title: 'Support',
		},
	],
]

const routes: Record<string, Route> = Object.fromEntries(routeEntries)

export function disposeProtectedSession(): void {
	navigationSequence++
	shell?.dispose()
	shell = undefined
}

export async function navigateTo(path: string): Promise<void> {
	const sequence = ++navigationSequence
	const root = document.getElementById('app-root')
	if (!root) return

	const user = await getUser()
	if (sequence !== navigationSequence) return

	// Normalize trailing slashes without changing the root route.
	path = path.length > 1 ? path.replace(/\/+$/, '') : path
	const releaseMatch = /^\/collection\/([1-9]\d*)$/.exec(path)
	const releaseId =
		releaseMatch && Number.isSafeInteger(Number(releaseMatch[1]))
			? Number(releaseMatch[1])
			: undefined
	const settingsOverlay = resolveSettingsOverlay(path)
	const friendsOverlay = path === '/activity/friends'
	let basePath = releaseId
		? '/collection'
		: settingsOverlay
			? '/settings'
			: friendsOverlay
				? '/activity'
				: path
	let route = routes[basePath]

	// Resolve redirects before touching the current view or authenticated shell.
	if (user && (!route || path === '/login')) {
		path =
			user.settings?.homepage &&
			routes[user.settings.homepage]?.isProtected
				? user.settings.homepage
				: '/collection'
		route = routes[path]
		basePath = path
		history.replaceState(null, '', path)
	} else if (!user && (!route || route.isProtected)) {
		path = '/login'
		route = routes[path]
		basePath = path
		history.replaceState(null, '', path)
	}
	if (!route) return
	rememberAppRoute(path)

	publicAbort?.abort()
	publicPage?.dispose()
	publicPage = undefined
	publicAbort = undefined

	if (route.isProtected && user) {
		if (shell && shell.user._id !== user._id) {
			shell.dispose()
			shell = undefined
		}
		shell ??= new ProtectedLayout(root, user)
		initAppEvents()
		void flushPendingSettings()
		// Only needs settings, so don't wait for the page's collection to load.
		void maybeShowReleaseNotesDialog()
		await shell.show(basePath, route)
		if (sequence !== navigationSequence) return
		if (settingsOverlay) {
			history.replaceState(
				history.state,
				'',
				settingsOverlayPath(settingsOverlay),
			)
			if (settingsOverlay === 'app-settings')
				await new SettingsAppOverlayController(user).open()
			else openSettingsSupportOverlay(settingsOverlay)
		} else if (friendsOverlay) {
			document
				.querySelector<HTMLButtonElement>('[data-manage-friends]')
				?.click()
		} else if (releaseId) {
			try {
				let release = getCachedReleaseByReleaseId(releaseId)
				// Numeric search includes Discogs IDs; scan matches to avoid confusing
				// matching titles with ownership of the requested release.
				for (let offset = 0; !release; ) {
					const collection = await getDiscogsCollectionPage(
						offset,
						100,
						{ search: String(releaseId), includeHidden: true },
					)
					if (sequence !== navigationSequence) return
					release = collection.releases.find(
						(item) => item.data.id === releaseId,
					)
					if (collection.releases.length < 100) break
					offset += collection.releases.length
				}
				if (sequence !== navigationSequence) return
				openOverlay(
					release
						? renderRelease(release)
						: renderReleaseLazy(
								releaseId,
								undefined,
								undefined,
								undefined,
								true,
							),
				)
			} catch (error) {
				if (sequence !== navigationSequence) return
				console.error('Failed to restore release page', error)
				openOverlay({
					path,
					html: '<p role="alert">Could not load this release. Please reload to try again.</p>',
					options: { link: false },
				})
			}
		}
		return
	}
	shell?.dispose()
	shell = undefined
	closeAppEvents()
	publicAbort = new AbortController()
	const page = await route.component.mount({
		containerEl: root,
		user,
		signal: publicAbort.signal,
	})
	if (sequence !== navigationSequence) {
		page.dispose()
		return
	}
	publicPage = page
	window.scrollTo(0, 0)
}
